import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Package, Search, Save, X, Eye, FileText, Calendar, ChevronLeft, ChevronRight, Filter, Edit2, DollarSign, TrendingUp, Hash, Camera } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const GRNEntry = () => {
  const navigate = useNavigate();
  const [grns, setGrns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxGroups, setTaxGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('list'); // list, form, detail, edit
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [editingGRN, setEditingGRN] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const scannerRef = useRef(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showPriceEditor, setShowPriceEditor] = useState(false);
  // Serial number state for GRN
  const [serialModal, setSerialModal] = useState(null); // { itemIndex, productName, qty }
  const [serialInput, setSerialInput] = useState('');
  const [priceUpdates, setPriceUpdates] = useState({
    selling_price: '',
    selling_price_l2: '',
    selling_price_l3: '',
    selling_price_l4: '',
    selling_price_l5: ''
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Filter state - default to last 1 month
  const getDefaultDates = () => {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setDate(today.getDate() - 30);
    return {
      from: lastMonth.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0]
    };
  };

  const [filters, setFilters] = useState({
    ...getDefaultDates(),
    supplier_id: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const [formData, setFormData] = useState({
    supplier_id: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    invoice_time: '',
    notes: '',
    items: [],
    payment_status: 'credit',
    payment_mode: '',
    paid_amount: ''
  });

  const [currentItem, setCurrentItem] = useState({
    product_id: '',
    product_name: '',
    quantity: '',
    free_qty: '',
    base_price: '',
    mrp: '',
    batch_number: '',
    expire_date: '',
    discount_percent: 0,
    discount_amount: 0
  });

  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
    fetchTaxGroups();
  }, []);

  useEffect(() => {
    fetchGRNs();
  }, [pagination.page, filters]);

  const fetchGRNs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      if (filters.from) params.from_date = filters.from;
      if (filters.to) params.to_date = filters.to;
      if (filters.supplier_id) params.supplier_id = filters.supplier_id;

      const response = await axios.get(`${API}/grn`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      // Handle both old format (array) and new format (object with pagination)
      if (Array.isArray(response.data)) {
        setGrns(response.data);
        setPagination(prev => ({ ...prev, total: response.data.length, totalPages: 1 }));
      } else {
        setGrns(response.data.items || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.total || 0,
          totalPages: response.data.total_pages || 1
        }));
      }
    } catch (err) {
      console.error('Error fetching GRNs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/products/enhanced`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchTaxGroups = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/tax-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaxGroups(response.data || []);
    } catch (err) {
      console.error('Error fetching tax groups:', err);
    }
  };

  const filteredProducts = products.filter(p => {
    const q = productSearch.toLowerCase().trim();
    if (!q) return false;
    return (
      (p.name?.toLowerCase().includes(q) ||
       p.sku?.toLowerCase().includes(q) ||
       p.barcode?.toLowerCase() === q ||
       p.barcode?.toLowerCase().includes(q)) &&
      !formData.items.find(i => i.product_id === p.id)
    );
  });

  const selectProduct = (product) => {
    setCurrentItem({
      ...currentItem,
      product_id: product.id,
      product_name: product.name,
      base_price: product.base_price?.toString() || '',
      mrp: product.mrp?.toString() || ''
    });
    setProductSearch(product.name);
    setShowProductDropdown(false);
    // Set selected product for price reference
    setSelectedProduct(product);
    // Pre-fill price updates with current values
    setPriceUpdates({
      selling_price: product.selling_price?.toString() || '',
      selling_price_l2: product.selling_price_l2?.toString() || '',
      selling_price_l3: product.selling_price_l3?.toString() || '',
      selling_price_l4: product.selling_price_l4?.toString() || '',
      selling_price_l5: product.selling_price_l5?.toString() || ''
    });
  };

  // Calculate profit percentage
  const calculateProfit = (costPrice, sellingPrice) => {
    if (!costPrice || costPrice <= 0) return 0;
    return ((sellingPrice - costPrice) / costPrice * 100).toFixed(1);
  };

  // Update product prices
  const updateProductPrices = async () => {
    if (!selectedProduct) return;
    
    try {
      const token = localStorage.getItem('pos_token');
      const updateData = {
        selling_price: parseFloat(priceUpdates.selling_price) || selectedProduct.selling_price,
        selling_price_l2: priceUpdates.selling_price_l2 ? parseFloat(priceUpdates.selling_price_l2) : null,
        selling_price_l3: priceUpdates.selling_price_l3 ? parseFloat(priceUpdates.selling_price_l3) : null,
        selling_price_l4: priceUpdates.selling_price_l4 ? parseFloat(priceUpdates.selling_price_l4) : null,
        selling_price_l5: priceUpdates.selling_price_l5 ? parseFloat(priceUpdates.selling_price_l5) : null
      };
      
      await axios.put(`${API}/products/${selectedProduct.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local product state
      setProducts(products.map(p => 
        p.id === selectedProduct.id 
          ? { ...p, ...updateData }
          : p
      ));
      setSelectedProduct({ ...selectedProduct, ...updateData });
      setShowPriceEditor(false);
      setSuccess('Product prices updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating prices:', err);
      setError('Failed to update prices');
    }
  };

  const calculateNetAmount = () => {
    const qty = parseFloat(currentItem.quantity) || 0;
    const price = parseFloat(currentItem.base_price) || 0;
    const discPercent = parseFloat(currentItem.discount_percent) || 0;
    const gross = qty * price;
    const discount = (gross * discPercent) / 100;
    return gross - discount;
  };

  const addItem = () => {
    if (!currentItem.product_id || !currentItem.quantity) {
      setError('Please select product and enter quantity');
      return;
    }
    // Allow base_price 0 for free items, but MRP is required
    if (currentItem.base_price === '' && !currentItem.free_qty) {
      setError('Please enter purchase price or mark as free qty');
      return;
    }

    const freeQty = parseFloat(currentItem.free_qty) || 0;
    const totalStockQty = parseFloat(currentItem.quantity) + freeQty;
    const netAmount = calculateNetAmount();
    const newItem = {
      ...currentItem,
      quantity: parseFloat(currentItem.quantity),
      free_qty: freeQty,
      total_stock_qty: totalStockQty,
      base_price: parseFloat(currentItem.base_price) || 0,
      mrp: parseFloat(currentItem.mrp) || 0,
      discount_percent: parseFloat(currentItem.discount_percent) || 0,
      discount_amount: parseFloat(currentItem.discount_amount) || 0,
      net_amount: netAmount,
      serial_numbers: []
    };
    
    const newItems = [...formData.items, newItem];
    setFormData({ ...formData, items: newItems });
    
    // Check if this product has serial numbers enabled
    const product = products.find(p => p.id === currentItem.product_id);
    if (product?.has_serial_number) {
      const qty = parseInt(currentItem.quantity);
      setSerialModal({ itemIndex: newItems.length - 1, productName: currentItem.product_name, qty });
      setSerialInput('');
    }

    // Reset current item and selected product
    setCurrentItem({
      product_id: '',
      product_name: '',
      quantity: '',
      free_qty: '',
      base_price: '',
      mrp: '',
      batch_number: '',
      expire_date: '',
      discount_percent: 0,
      discount_amount: 0
    });
    setProductSearch('');
    setSelectedProduct(null);
    setShowPriceEditor(false);
    setError('');
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async () => {
    if (!formData.supplier_id) {
      setError('Please select a supplier');
      return;
    }
    if (formData.items.length === 0) {
      setError('Please add at least one item');
      return;
    }

    // Validate payment
    const totalAmount = formData.items.reduce((sum, item) => sum + (item.net_amount || 0), 0);
    const paidAmount = parseFloat(formData.paid_amount) || 0;

    if (formData.payment_status === 'paid' && paidAmount < totalAmount) {
      setError('Paid amount must equal total amount for "Paid" status');
      return;
    }
    if (formData.payment_status === 'partial') {
      if (paidAmount <= 0 || paidAmount >= totalAmount) {
        setError('Partial payment must be greater than 0 and less than total amount');
        return;
      }
    }
    if ((formData.payment_status === 'paid' || formData.payment_status === 'partial') && !formData.payment_mode) {
      setError('Please select payment mode');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('pos_token');
      
      const payload = {
        ...formData,
        invoice_date: formData.invoice_time 
          ? `${formData.invoice_date}T${formData.invoice_time}:00` 
          : formData.invoice_date,
        total_amount: totalAmount,
        discount_amount: 0,
        net_amount: totalAmount,
        paid_amount: paidAmount,
        payment_mode: formData.payment_mode || null
      };
      
      await axios.post(`${API}/grn`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('GRN created successfully! Stock has been updated.');
      resetForm();
      fetchGRNs();
      setView('list');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create GRN');
    } finally {
      setSaving(false);
    }
  };

  const handleEditGRN = (grn, e) => {
    e.stopPropagation(); // Prevent row click
    setEditingGRN(grn);
    setFormData({
      supplier_id: grn.supplier_id || '',
      invoice_number: grn.invoice_number || '',
      invoice_date: grn.invoice_date || new Date().toISOString().split('T')[0],
      notes: grn.notes || '',
      items: grn.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantity,
        base_price: item.purchase_price || item.base_price,
        mrp: item.mrp || item.selling_price,
        batch_number: item.batch_number || '',
        expire_date: item.expire_date || '',
        discount_percent: item.discount_percent || 0,
        discount_amount: item.discount_amount || 0,
        net_amount: item.total || (item.quantity * (item.purchase_price || item.base_price))
      })),
      payment_status: grn.payment_status || 'credit',
      payment_mode: grn.payment_mode || '',
      paid_amount: grn.paid_amount || ''
    });
    setView('edit');
  };

  const handleUpdateGRN = async () => {
    if (!editingGRN) return;
    if (formData.items.length === 0) {
      setError('Please add at least one item');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('pos_token');
      
      const updateData = {
        supplier_id: formData.supplier_id || null,
        notes: formData.notes,
        items: formData.items.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: parseFloat(item.quantity),
          purchase_price: parseFloat(item.base_price),
          selling_price: parseFloat(item.mrp) || null
        }))
      };

      await axios.put(`${API}/grn/${editingGRN.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('GRN updated successfully! Stock and supplier ledger adjusted.');
      setTimeout(() => setSuccess(''), 3000);
      fetchGRNs();
      resetForm();
      setEditingGRN(null);
      setView('list');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update GRN');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '',
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      notes: '',
      items: [],
      payment_status: 'credit',
      payment_mode: '',
      paid_amount: ''
    });
    setCurrentItem({
      product_id: '',
      product_name: '',
      quantity: '',
      base_price: '',
      mrp: '',
      batch_number: '',
      expire_date: '',
      discount_percent: 0,
      discount_amount: 0
    });
    setProductSearch('');
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    setShowFilters(false);
  };

  const resetFilters = () => {
    setFilters({ ...getDefaultDates(), supplier_id: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toFixed(2)}`;
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + 
      ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const totalAmount = formData.items.reduce((sum, item) => sum + (item.net_amount || 0), 0);

  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Paid</span>;
      case 'partial':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Partial</span>;
      default:
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Credit</span>;
    }
  };

  if (loading && view === 'list') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  // GRN Detail View
  if (view === 'detail' && selectedGRN) {
    return (
      <div data-testid="grn-detail-page" className="space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={() => { setView('list'); setSelectedGRN(null); }} className="p-2 hover:bg-slate-100 rounded-xl">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">GRN #{selectedGRN.grn_number}</h1>
            <p className="text-slate-500">{formatDate(selectedGRN.created_at)}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div>
              <p className="text-sm text-slate-500">Supplier</p>
              <p className="font-medium">{selectedGRN.supplier_name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Invoice #</p>
              <p className="font-medium">{selectedGRN.invoice_number || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Invoice Date</p>
              <p className="font-medium">{formatDate(selectedGRN.invoice_date)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Payment Status</p>
              {getPaymentStatusBadge(selectedGRN.payment_status)}
            </div>
            <div>
              <p className="text-sm text-slate-500">Total</p>
              <p className="font-bold text-violet-600">{formatCurrency(selectedGRN.net_amount)}</p>
            </div>
          </div>

          {/* Payment Details */}
          {selectedGRN.payment_status !== 'credit' && (
            <div className="mb-6 p-4 bg-slate-50 rounded-xl">
              <h4 className="font-medium text-slate-800 mb-2">Payment Details</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Paid Amount</p>
                  <p className="font-semibold text-green-600">{formatCurrency(selectedGRN.paid_amount)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Balance</p>
                  <p className="font-semibold text-red-600">{formatCurrency(selectedGRN.balance_amount)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Mode</p>
                  <p className="font-medium capitalize">{selectedGRN.payment_mode || '-'}</p>
                </div>
              </div>
            </div>
          )}

          <h3 className="font-semibold mb-3">Items ({selectedGRN.items?.length || 0})</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right text-emerald-600">Free</th>
                  <th className="px-3 py-2 text-right">Cost</th>
                  <th className="px-3 py-2 text-right">MRP</th>
                  <th className="px-3 py-2 text-left">Batch</th>
                  <th className="px-3 py-2 text-left">Expiry</th>
                  <th className="px-3 py-2 text-right">Net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {selectedGRN.items?.map((item, idx) => (
                  <tr key={`item-row-${idx}`}>
                    <td className="px-3 py-2 font-medium">{item.product_name}</td>
                    <td className="px-3 py-2 text-right">{item.quantity}</td>
                    <td className="px-3 py-2 text-right text-emerald-600">{item.free_qty || 0}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(item.base_price)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(item.mrp)}</td>
                    <td className="px-3 py-2">{item.batch_number || '-'}</td>
                    <td className="px-3 py-2">{item.expire_date || '-'}</td>
                    <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.net_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // GRN Edit View - similar to form but for editing
  if (view === 'edit' && editingGRN) {
    return (
      <div data-testid="grn-edit-page" className="space-y-4 pb-24">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button onClick={() => { setView('list'); resetForm(); setEditingGRN(null); }} className="p-2 hover:bg-slate-100 rounded-xl">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-800">Edit GRN #{editingGRN.grn_number}</h1>
            <p className="text-sm text-slate-500">Stock and supplier ledger will be adjusted</p>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
            <X size={16} /> {error}
          </div>
        )}

        {success && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
            {success}
          </div>
        )}

        {/* Supplier Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
          <h3 className="font-semibold text-slate-800">Invoice Details</h3>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Supplier</label>
            <select
              value={formData.supplier_id}
              onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 text-base"
            >
              <option value="">Select Supplier</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optional notes..."
              rows={2}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        </div>

        {/* Items Edit Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
          <h3 className="font-semibold text-slate-800">Items ({formData.items.length})</h3>
          
          {formData.items.map((item, index) => (
            <div key={item.product_id} className="p-3 bg-slate-50 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <p className="font-medium text-slate-800">{item.product_name}</p>
                <button
                  onClick={() => setFormData({
                    ...formData,
                    items: formData.items.filter((_, i) => i !== index)
                  })}
                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-slate-500">Quantity</label>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => {
                      const newItems = [...formData.items];
                      newItems[index].quantity = e.target.value;
                      newItems[index].net_amount = parseFloat(e.target.value || 0) * parseFloat(item.base_price || 0);
                      setFormData({ ...formData, items: newItems });
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-emerald-600">Free Qty</label>
                  <input
                    type="number"
                    value={item.free_qty || ''}
                    onChange={(e) => {
                      const newItems = [...formData.items];
                      newItems[index].free_qty = e.target.value;
                      newItems[index].total_stock_qty = parseFloat(item.quantity || 0) + parseFloat(e.target.value || 0);
                      setFormData({ ...formData, items: newItems });
                    }}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm bg-emerald-50"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Purchase Price</label>
                  <input
                    type="number"
                    value={item.base_price}
                    onChange={(e) => {
                      const newItems = [...formData.items];
                      newItems[index].base_price = e.target.value;
                      newItems[index].net_amount = parseFloat(item.quantity || 0) * parseFloat(e.target.value || 0);
                      setFormData({ ...formData, items: newItems });
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </div>
              <p className="text-right text-sm font-medium text-violet-600">
                Total: {formatCurrency(item.net_amount)}
              </p>
            </div>
          ))}

          {formData.items.length === 0 && (
            <p className="text-center text-slate-500 py-4">No items in this GRN</p>
          )}
        </div>

        {/* Total */}
        <div className="bg-violet-50 rounded-xl p-4">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-slate-700">Total Amount</span>
            <span className="text-2xl font-bold text-violet-600">
              {formatCurrency(formData.items.reduce((sum, item) => sum + (item.net_amount || 0), 0))}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 flex gap-3">
          <button
            onClick={() => { setView('list'); resetForm(); setEditingGRN(null); }}
            className="flex-1 py-3 border border-slate-200 rounded-xl text-slate-600 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdateGRN}
            disabled={saving || formData.items.length === 0}
            className="flex-1 py-3 bg-violet-600 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? 'Updating...' : (
              <>
                <Save size={18} />
                Update GRN
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // GRN Form View (Full Page - Mobile Friendly)
  if (view === 'form') {
    return (
      <>
      <div data-testid="grn-form-page" className="space-y-4 pb-24">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button onClick={() => { setView('list'); resetForm(); }} className="p-2 hover:bg-slate-100 rounded-xl">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-800">New GRN Entry</h1>
            <p className="text-sm text-slate-500">Goods Received Note</p>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
            <X size={16} /> {error}
          </div>
        )}

        {success && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm flex items-center gap-2">
            <Package size={16} /> {success}
          </div>
        )}

        {/* Supplier & Invoice Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
          <h3 className="font-semibold text-slate-800">Invoice Details</h3>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Supplier *</label>
            <select
              value={formData.supplier_id}
              onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 text-base"
              data-testid="grn-supplier-select"
            >
              <option value="">Select Supplier</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Invoice #</label>
              <input
                type="text"
                value={formData.invoice_number}
                onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                placeholder="INV-001"
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Date</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={formData.invoice_date}
                  onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                <input
                  type="time"
                  value={formData.invoice_time}
                  onChange={(e) => setFormData({ ...formData, invoice_time: e.target.value })}
                  placeholder="Time (optional)"
                  className="w-32 px-3 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">Time is optional</p>
            </div>
          </div>
        </div>

        {/* Add Item Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
          <h3 className="font-semibold text-slate-800">Add Items</h3>
          
          {/* Product Search */}
          <div className="relative">
            <label className="block text-sm font-medium text-slate-700 mb-1">Product * <span className="text-xs text-slate-400 font-normal">(name, SKU, or barcode)</span></label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                  onFocus={() => setShowProductDropdown(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const q = productSearch.trim();
                      if (!q) return;
                      // Exact barcode / SKU match → auto-select
                      const exactMatch = products.find(p =>
                        (p.barcode && p.barcode === q) || (p.sku && p.sku === q)
                      );
                      const target = exactMatch || filteredProducts[0];
                      if (target && !formData.items.find(i => i.product_id === target.id)) {
                        selectProduct(target);
                      }
                    } else if (e.key === 'Escape') {
                      setShowProductDropdown(false);
                    }
                  }}
                  placeholder="Search by name, SKU, or scan barcode..."
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="grn-product-search"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowScanner(true)}
                className="h-[46px] px-3 bg-slate-700 text-white rounded-xl hover:bg-slate-800 flex items-center justify-center"
                data-testid="grn-barcode-scan-btn"
                title="Scan barcode with camera"
              >
                <Camera size={18} />
              </button>
            </div>
            {showProductDropdown && productSearch && filteredProducts.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                {filteredProducts.slice(0, 10).map(product => (
                  <div
                    key={product.id}
                    onClick={() => selectProduct(product)}
                    className="px-4 py-3 hover:bg-violet-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                  >
                    <p className="font-medium text-sm">{product.name}</p>
                    <p className="text-xs text-slate-500">SKU: {product.sku} | Stock: {product.current_stock}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quantity & Prices */}
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Qty *</label>
              <input
                type="number"
                value={currentItem.quantity}
                onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 text-center"
                data-testid="grn-qty-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Free Qty</label>
              <input
                type="number"
                value={currentItem.free_qty}
                onChange={(e) => setCurrentItem({ ...currentItem, free_qty: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-3 border border-emerald-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-center bg-emerald-50"
                data-testid="grn-free-qty-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Cost</label>
              <input
                type="number"
                step="0.01"
                value={currentItem.base_price}
                onChange={(e) => setCurrentItem({ ...currentItem, base_price: e.target.value })}
                placeholder="0.00"
                className="w-full px-3 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 text-center"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">MRP</label>
              <input
                type="number"
                step="0.01"
                value={currentItem.mrp}
                onChange={(e) => setCurrentItem({ ...currentItem, mrp: e.target.value })}
                placeholder="0.00"
                className="w-full px-3 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 text-center"
              />
            </div>
          </div>

          {/* GST Split Display - shows base price, GST, and total with GST */}
          {currentItem.base_price && selectedProduct && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-xs font-medium text-slate-500 mb-2">Price Breakdown (per unit)</p>
              {(() => {
                const taxGroup = taxGroups.find(t => t.id === selectedProduct.tax_group_id);
                const gstRate = taxGroup ? ((taxGroup.cgst || 0) + (taxGroup.sgst || 0)) : 0;
                const basePrice = parseFloat(currentItem.base_price) || 0;
                const gstAmount = basePrice * gstRate / 100;
                const totalWithGST = basePrice + gstAmount;
                return (
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div><p className="text-xs text-slate-400">Base Price</p><p className="text-sm font-bold text-slate-800">Rs.{basePrice.toFixed(2)}</p></div>
                    <div><p className="text-xs text-slate-400">GST ({gstRate}%)</p><p className="text-sm font-bold text-amber-600">Rs.{gstAmount.toFixed(2)}</p></div>
                    <div><p className="text-xs text-slate-400">Total w/ GST</p><p className="text-sm font-bold text-violet-700">Rs.{totalWithGST.toFixed(2)}</p></div>
                    <div><p className="text-xs text-slate-400">Net Amount</p><p className="text-sm font-bold text-emerald-700">Rs.{(basePrice * (parseFloat(currentItem.quantity) || 0)).toFixed(2)}</p></div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Current Price Info Card - Shows when product is selected */}
          {selectedProduct && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign size={18} className="text-blue-600" />
                  <span className="font-semibold text-slate-800">Current Pricing</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPriceEditor(!showPriceEditor)}
                  className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
                >
                  <Edit2 size={14} />
                  {showPriceEditor ? 'Close' : 'Update Prices'}
                </button>
              </div>
              
              {/* Reference Info */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-white/70 rounded-lg p-2">
                  <p className="text-xs text-slate-500">Last Cost</p>
                  <p className="font-bold text-slate-700">₹{selectedProduct.base_price?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="bg-white/70 rounded-lg p-2">
                  <p className="text-xs text-slate-500">Last MRP</p>
                  <p className="font-bold text-slate-700">₹{selectedProduct.mrp?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="bg-white/70 rounded-lg p-2">
                  <p className="text-xs text-slate-500">Current Stock</p>
                  <p className="font-bold text-slate-700">{selectedProduct.current_stock || 0}</p>
                </div>
              </div>

              {/* Price Levels with Profit % */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-600 flex items-center gap-1">
                  <TrendingUp size={14} />
                  Selling Prices & Profit Margins
                </p>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { label: 'L1', price: selectedProduct.selling_price },
                    { label: 'L2', price: selectedProduct.selling_price_l2 },
                    { label: 'L3', price: selectedProduct.selling_price_l3 },
                    { label: 'L4', price: selectedProduct.selling_price_l4 },
                    { label: 'L5', price: selectedProduct.selling_price_l5 }
                  ].map((level, idx) => (
                    <div key={`level-${idx}`} className={`text-center rounded-lg p-2 ${level.price ? 'bg-white' : 'bg-slate-100'}`}>
                      <p className="text-xs text-slate-500">{level.label}</p>
                      {level.price ? (
                        <>
                          <p className="font-bold text-sm text-slate-800">₹{level.price.toFixed(0)}</p>
                          <p className={`text-xs ${parseFloat(calculateProfit(currentItem.base_price || selectedProduct.base_price, level.price)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {calculateProfit(currentItem.base_price || selectedProduct.base_price, level.price)}%
                          </p>
                        </>
                      ) : (
                        <p className="text-xs text-slate-400">Not set</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Editor */}
              {showPriceEditor && (
                <div className="mt-3 pt-3 border-t border-blue-200 space-y-3">
                  <p className="text-xs font-medium text-blue-700">Update Selling Prices (based on new cost: ₹{currentItem.base_price || selectedProduct.base_price})</p>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { key: 'selling_price', label: 'Level 1' },
                      { key: 'selling_price_l2', label: 'Level 2' },
                      { key: 'selling_price_l3', label: 'Level 3' },
                      { key: 'selling_price_l4', label: 'Level 4' },
                      { key: 'selling_price_l5', label: 'Level 5' }
                    ].map((level) => (
                      <div key={level.key}>
                        <label className="block text-xs text-slate-500 mb-1">{level.label}</label>
                        <input
                          type="number"
                          step="0.01"
                          value={priceUpdates[level.key]}
                          onChange={(e) => setPriceUpdates({ ...priceUpdates, [level.key]: e.target.value })}
                          placeholder="0"
                          className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {priceUpdates[level.key] && (
                          <p className={`text-xs text-center mt-1 ${parseFloat(calculateProfit(currentItem.base_price || selectedProduct.base_price, priceUpdates[level.key])) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {calculateProfit(currentItem.base_price || selectedProduct.base_price, priceUpdates[level.key])}%
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={updateProductPrices}
                    className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
                    Save Price Updates
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Batch & Expiry */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Batch #</label>
              <input
                type="text"
                value={currentItem.batch_number}
                onChange={(e) => setCurrentItem({ ...currentItem, batch_number: e.target.value })}
                placeholder="Optional"
                className="w-full px-3 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Expiry Date</label>
              <input
                type="date"
                value={currentItem.expire_date}
                onChange={(e) => setCurrentItem({ ...currentItem, expire_date: e.target.value })}
                className="w-full px-3 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          </div>

          {/* Discount */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Discount %</label>
              <input
                type="number"
                step="0.01"
                value={currentItem.discount_percent}
                onChange={(e) => setCurrentItem({ ...currentItem, discount_percent: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 text-center"
              />
            </div>
            <div className="flex items-end">
              <div className="w-full px-4 py-3 bg-violet-50 rounded-xl text-center">
                <p className="text-xs text-violet-600">Net Amount</p>
                <p className="font-bold text-violet-700">{formatCurrency(calculateNetAmount())}</p>
              </div>
            </div>
          </div>

          <button
            onClick={addItem}
            disabled={!currentItem.product_id}
            className="w-full py-3 bg-violet-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="grn-add-item-btn"
          >
            <Plus size={20} />
            Add Item to List
          </button>
        </div>

        {/* Items List */}
        {formData.items.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
              <h3 className="font-semibold">Items ({formData.items.length})</h3>
              <p className="font-bold text-violet-600">Total: {formatCurrency(totalAmount)}</p>
            </div>
            <div className="divide-y divide-slate-100">
              {formData.items.map((item, idx) => (
                <div key={`grn-item-${idx}`} className="p-4 flex items-center gap-3" data-testid={`grn-item-${idx}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{item.product_name}</p>
                    <p className="text-xs text-slate-500">
                      {item.quantity} × {formatCurrency(item.base_price)} 
                      {item.batch_number ? ` | Batch: ${item.batch_number}` : ''}
                      {item.expire_date ? ` | Exp: ${item.expire_date}` : ''}
                      {item.serial_numbers?.length > 0 ? ` | SN: ${item.serial_numbers.join(', ')}` : ''}
                    </p>
                    {(() => {
                      const prod = products.find(p => p.id === item.product_id);
                      if (prod?.has_serial_number && (!item.serial_numbers || item.serial_numbers.length === 0)) {
                        return <button onClick={() => { setSerialModal({ itemIndex: idx, productName: item.product_name, qty: Math.floor(item.quantity) }); setSerialInput(''); }} className="text-xs text-orange-600 hover:underline flex items-center gap-1 mt-0.5"><Hash size={12} />Add Serial Numbers</button>;
                      }
                      return null;
                    })()}
                  </div>
                  <p className="font-semibold text-violet-600 whitespace-nowrap">{formatCurrency(item.net_amount)}</p>
                  <button onClick={() => removeItem(idx)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Section */}
        {formData.items.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
            <h3 className="font-semibold text-slate-800">Payment Details</h3>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Payment Status *</label>
              <div className="flex gap-2">
                {[
                  { value: 'credit', label: 'Credit', color: 'red' },
                  { value: 'paid', label: 'Paid', color: 'green' },
                  { value: 'partial', label: 'Partial', color: 'amber' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        payment_status: opt.value,
                        paid_amount: opt.value === 'paid' ? totalAmount.toString() : (opt.value === 'credit' ? '0' : formData.paid_amount),
                        payment_mode: opt.value === 'credit' ? '' : formData.payment_mode
                      });
                    }}
                    className={`flex-1 py-3 rounded-xl font-medium border-2 transition-colors ${
                      formData.payment_status === opt.value
                        ? `bg-${opt.color}-100 border-${opt.color}-500 text-${opt.color}-700`
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {formData.payment_status !== 'credit' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode *</label>
                  <select
                    value={formData.payment_mode}
                    onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                  >
                    <option value="">Select Mode</option>
                    <option value="cash">Cash</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="upi">UPI</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Paid Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.paid_amount}
                    onChange={(e) => setFormData({ ...formData, paid_amount: e.target.value })}
                    placeholder="0.00"
                    disabled={formData.payment_status === 'paid'}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:bg-slate-50"
                  />
                </div>
              </div>
            )}

            {formData.payment_status !== 'credit' && (
              <div className="p-3 bg-slate-50 rounded-xl">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Total Amount:</span>
                  <span className="font-semibold">{formatCurrency(totalAmount)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-slate-600">Paid Amount:</span>
                  <span className="font-semibold text-green-600">{formatCurrency(parseFloat(formData.paid_amount) || 0)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1 pt-2 border-t border-slate-200">
                  <span className="text-slate-600">Balance Due:</span>
                  <span className="font-semibold text-red-600">{formatCurrency(totalAmount - (parseFloat(formData.paid_amount) || 0))}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Notes (Optional)</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={2}
            placeholder="Any additional notes..."
            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
          />
        </div>

        {/* Fixed Bottom Submit Button */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-200 shadow-lg z-40">
          <div className="max-w-2xl mx-auto flex gap-3">
            <button
              onClick={() => { setView('list'); resetForm(); }}
              className="flex-1 py-3 border border-slate-200 rounded-xl font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || formData.items.length === 0}
              className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="grn-submit-btn"
            >
              <Save size={20} />
              {saving ? 'Saving...' : `Save GRN (${formatCurrency(totalAmount)})`}
            </button>
          </div>
        </div>
      </div>

      {/* Serial Number Entry Modal for GRN */}
      {serialModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="grn-serial-modal">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800">Enter Serial Numbers</h3>
              <p className="text-sm text-slate-500">{serialModal.productName} - Qty: {serialModal.qty}</p>
            </div>
            <div className="p-4">
              <label className="text-xs font-semibold text-slate-600 block mb-1">
                Enter {serialModal.qty} serial number(s), one per line
              </label>
              <textarea
                value={serialInput}
                onChange={e => setSerialInput(e.target.value)}
                rows={Math.min(serialModal.qty + 1, 8)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-orange-500 focus:outline-none"
                placeholder={`SN001\nSN002\n...`}
                autoFocus
                data-testid="grn-serial-input"
              />
              {serialInput && (
                <p className="text-xs text-slate-500 mt-1">
                  Entered: {serialInput.split('\n').filter(s => s.trim()).length} / {serialModal.qty} required
                </p>
              )}
            </div>
            <div className="flex gap-2 p-4 border-t border-slate-200">
              <button
                onClick={() => {
                  setSerialModal(null);
                  setSerialInput('');
                }}
                className="flex-1 py-2 border border-slate-200 rounded-lg text-sm font-medium"
              >
                Skip (Add later)
              </button>
              <button
                onClick={() => {
                  const serials = serialInput.split('\n').map(s => s.trim()).filter(s => s);
                  if (serials.length !== serialModal.qty) {
                    alert(`Please enter exactly ${serialModal.qty} serial numbers. You entered ${serials.length}.`);
                    return;
                  }
                  const updatedItems = [...formData.items];
                  updatedItems[serialModal.itemIndex] = {
                    ...updatedItems[serialModal.itemIndex],
                    serial_numbers: serials
                  };
                  setFormData({ ...formData, items: updatedItems });
                  setSerialModal(null);
                  setSerialInput('');
                }}
                disabled={!serialInput.trim()}
                className="flex-1 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-40"
                data-testid="grn-serial-save"
              >
                Save Serials
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    );
  }

  // GRN List View
  return (
    <div data-testid="grn-list-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">GRN Entry</h1>
          <p className="text-slate-500">Manage goods received from suppliers</p>
        </div>
        <button
          onClick={() => setView('form')}
          className="bg-gradient-to-r from-violet-600 to-purple-600 text-white px-6 py-2.5 rounded-xl font-medium hover:shadow-lg transition-all flex items-center gap-2"
          data-testid="new-grn-btn"
        >
          <Plus size={20} />
          New GRN
        </button>
      </div>

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 flex items-center gap-2">
          <Package size={20} />
          {success}
        </div>
      )}

      {/* Filters Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
          >
            <Filter size={18} />
            Filters
          </button>
          
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Calendar size={16} />
            {filters.from} to {filters.to}
            {filters.supplier_id && ` | ${suppliers.find(s => s.id === filters.supplier_id)?.name || 'Supplier'}`}
          </div>

          <button
            onClick={resetFilters}
            className="text-sm text-violet-600 hover:underline"
          >
            Reset to Default
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">From Date</label>
              <input
                type="date"
                value={filters.from}
                onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">To Date</label>
              <input
                type="date"
                value={filters.to}
                onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Supplier</label>
              <select
                value={filters.supplier_id}
                onChange={(e) => setFilters({ ...filters, supplier_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="">All Suppliers</option>
                {suppliers.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={applyFilters}
                className="w-full py-2 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* GRN List - Table Grid Format */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {grns.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="mx-auto mb-4 text-slate-300" size={48} />
            <p className="text-slate-500 mb-4">No GRN entries found</p>
            <button
              onClick={() => setView('form')}
              className="px-6 py-2 bg-violet-600 text-white rounded-lg"
            >
              Create First GRN
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">GRN #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Supplier</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Invoice #</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Items</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Balance</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {grns.map((grn) => (
                    <tr 
                      key={grn.id} 
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`grn-row-${grn.id}`}
                    >
                      <td className="px-4 py-3">
                        <span className="font-mono font-semibold text-violet-600">#{grn.grn_number}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {formatDate(grn.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-800">{grn.supplier_name || '-'}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {grn.invoice_number || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs font-medium">
                          {grn.items?.length || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800">
                        {formatCurrency(grn.net_amount)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getPaymentStatusBadge(grn.payment_status)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {grn.payment_status !== 'paid' && grn.balance_amount > 0 ? (
                          <span className="font-semibold text-red-600">{formatCurrency(grn.balance_amount)}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => { setSelectedGRN(grn); setView('detail'); }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={(e) => handleEditGRN(grn, e)}
                            className="p-2 text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                            title="Edit GRN"
                          >
                            <Edit2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="p-4 border-t border-slate-200 flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} records)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="p-2 border border-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="p-2 border border-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Barcode Scanner Modal */}
      {showScanner && (
        <BarcodeScanModal
          onClose={() => setShowScanner(false)}
          onScan={(code) => {
            setShowScanner(false);
            const trimmed = (code || '').trim();
            if (!trimmed) return;
            const match = products.find(p =>
              (p.barcode && p.barcode === trimmed) || (p.sku && p.sku === trimmed)
            );
            if (match) {
              if (!formData.items.find(i => i.product_id === match.id)) {
                selectProduct(match);
              } else {
                alert('Product already added to GRN');
              }
            } else {
              setProductSearch(trimmed);
              setShowProductDropdown(true);
              alert(`No product found with barcode/SKU: ${trimmed}`);
            }
          }}
          scannerRef={scannerRef}
        />
      )}
    </div>
  );
};

// Barcode Scanner Modal (camera-based) — uses html5-qrcode
const BarcodeScanModal = ({ onClose, onScan, scannerRef }) => {
  const [error, setError] = useState('');

  useEffect(() => {
    const scanner = new Html5Qrcode('grn-qr-reader');
    scannerRef.current = scanner;
    scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 250, height: 150 } },
      (decoded) => {
        onScan(decoded);
        scanner.stop().then(() => scanner.clear()).catch(() => {});
      },
      () => {}
    ).catch(err => {
      console.error('Camera start error:', err);
      setError('Could not access camera. Please allow camera permission.');
    });

    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().then(() => scannerRef.current.clear()).catch(() => {});
        scannerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="grn-scanner-modal">
      <div className="bg-white rounded-2xl p-4 w-full max-w-md">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Scan Barcode</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="grn-scanner-close">
            <X size={20} />
          </button>
        </div>
        {error && <div className="bg-red-50 text-red-700 text-sm p-2 rounded mb-2">{error}</div>}
        <div id="grn-qr-reader" className="w-full"></div>
        <p className="text-xs text-slate-500 mt-3 text-center">Point camera at barcode. Tap close when done.</p>
      </div>
    </div>
  );
};

export default GRNEntry;
